import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { mergeRouteIntoBriefing } from "@/lib/routeSync";
import { ClipboardList, Plus, Trash2, Plane, Route as RouteIcon, ArrowRight, Calendar, GraduationCap } from "lucide-react";

function fmtDateFr(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}
function composeName(route, navDate, navTime) {
  if (!route) return "";
  const dep = route.departure || "?";
  const arr = route.arrival || "?";
  const turns = (route.legs || []).slice(0, -1).map((l) => l.name).filter(Boolean);
  const mid = turns.length ? ` (${turns.join(" ")})` : "";
  const dt = [fmtDateFr(navDate), navTime].filter(Boolean).join(" ");
  return `${dep}${mid} - ${arr}${dt ? " · " + dt : ""}`;
}

export default function BriefingList() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [shared, setShared] = useState([]);
  const [aircraft, setAircraft] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [pilots, setPilots] = useState([]);
  const [open, setOpen] = useState(false);
  const [nb, setNb] = useState({
    name: "", aircraftId: "", routeId: "", pilotId: "",
    navDate: new Date().toISOString().slice(0, 10), navTime: "10:00",
    nameEdited: false,
  });

  const load = async () => {
    const [b, a, r, s, p] = await Promise.all([
      api.get("/briefings"), api.get("/aircraft"), api.get("/routes"),
      api.get("/briefings/shared-with-me"), api.get("/pilots"),
    ]);
    setItems(b.data);
    setAircraft(a.data);
    setRoutes(r.data);
    setShared(s.data);
    setPilots(p.data);
  };
  useEffect(() => { load(); }, []);

  const applyRoute = (routeId) => {
    const route = routes.find((r) => r.id === routeId);
    setNb((s) => ({ ...s, routeId, name: s.nameEdited ? s.name : composeName(route, s.navDate, s.navTime) }));
  };
  const applyDateTime = (key, val) => {
    setNb((s) => {
      const next = { ...s, [key]: val };
      const route = routes.find((r) => r.id === s.routeId);
      return { ...next, name: s.nameEdited ? s.name : composeName(route, next.navDate, next.navTime) };
    });
  };

  const create = async () => {
    if (!nb.name) return toast.error("Nom du briefing requis");
    if (!nb.aircraftId) return toast.error("Sélectionnez un avion");
    if (!nb.routeId) return toast.error("Sélectionnez une route");
    const route = routes.find((r) => r.id === nb.routeId);
    const pilot = pilots.find((p) => p.id === nb.pilotId);
    const payload = mergeRouteIntoBriefing({
      name: nb.name,
      aircraftId: nb.aircraftId,
      routeId: nb.routeId,
      pilotId: nb.pilotId || "",
      pilotName: pilot ? `${pilot.firstName || ""} ${pilot.lastName || ""}`.trim() : "",
      pilotWeight: pilot ? Number(pilot.weight) || 0 : 0,
      navDate: nb.navDate,
      navTime: nb.navTime,
      fuel: { taxiCount: 1, taxiStd: 10, procedureCount: 1, procedureStd: 10, night: false, onboardL: 0 },
      weights: {},
      fuelStartL: 0,
      limitations: [],
      checklist: {},
    }, route || {});
    const { data } = await api.post("/briefings", payload);
    setOpen(false);
    navigate(`/briefings/${data.id}`);
  };

  const remove = async (id) => {
    await api.delete(`/briefings/${id}`);
    toast.success("Briefing supprimé");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-cyan-500/80">
            <ClipboardList className="h-3.5 w-3.5" /> Préparation
          </div>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-slate-50">Briefings</h1>
          <p className="mt-1 text-sm text-slate-400">Dossiers de navigation complets, checklist 11 points.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-briefing-button" className="bg-cyan-500 font-semibold text-[#070C18] hover:bg-cyan-400">
              <Plus className="mr-1.5 h-4 w-4" /> Nouveau briefing
            </Button>
          </DialogTrigger>
          <DialogContent className="border-slate-700 bg-[#0D1527] text-slate-100">
            <DialogHeader><DialogTitle className="font-display">Nouveau briefing</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-400">Avion</Label>
                <Select value={nb.aircraftId} onValueChange={(v) => setNb({ ...nb, aircraftId: v })}>
                  <SelectTrigger data-testid="briefing-aircraft-select" className="mt-1 border-slate-700 bg-slate-900/60 text-slate-100">
                    <SelectValue placeholder={aircraft.length ? "Sélectionner un avion" : "Aucun avion — créez-en un"} />
                  </SelectTrigger>
                  <SelectContent className="border-slate-700 bg-[#0D1527] text-slate-100">
                    {aircraft.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.registration} — {a.model}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-400">Route</Label>
                <Select value={nb.routeId} onValueChange={applyRoute}>
                  <SelectTrigger data-testid="briefing-route-select" className="mt-1 border-slate-700 bg-slate-900/60 text-slate-100">
                    <SelectValue placeholder={routes.length ? "Sélectionner une route" : "Aucune route — créez-en une"} />
                  </SelectTrigger>
                  <SelectContent className="border-slate-700 bg-[#0D1527] text-slate-100">
                    {routes.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.name} ({r.departure}→{r.arrival})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-400">Pilote (optionnel)</Label>
                <Select value={nb.pilotId} onValueChange={(v) => setNb({ ...nb, pilotId: v })}>
                  <SelectTrigger data-testid="briefing-pilot-select" className="mt-1 border-slate-700 bg-slate-900/60 text-slate-100">
                    <SelectValue placeholder={pilots.length ? "Sélectionner un pilote" : "Aucun pilote — créez-en un"} />
                  </SelectTrigger>
                  <SelectContent className="border-slate-700 bg-[#0D1527] text-slate-100">
                    {pilots.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.firstName} {p.lastName} — {p.weight || 0} kg</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-slate-400">Date de la nav</Label>
                  <Input type="date" value={nb.navDate} onChange={(e) => applyDateTime("navDate", e.target.value)} data-testid="briefing-date-input"
                    className="mt-1 border-slate-700 bg-slate-900/60 text-slate-100" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-slate-400">Heure</Label>
                  <Input type="time" value={nb.navTime} onChange={(e) => applyDateTime("navTime", e.target.value)} data-testid="briefing-time-input"
                    className="mt-1 border-slate-700 bg-slate-900/60 text-slate-100" />
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-400">Nom du briefing (proposé)</Label>
                <Input value={nb.name} onChange={(e) => setNb({ ...nb, name: e.target.value, nameEdited: true })} data-testid="briefing-name-input"
                  className="mt-1 border-slate-700 bg-slate-900/60 text-slate-100" placeholder="Sélectionnez une route pour composer le nom" />
                <p className="mt-1 text-[11px] text-slate-500">Composé automatiquement — modifiable librement.</p>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} data-testid="create-briefing-confirm" className="bg-cyan-500 font-semibold text-[#070C18] hover:bg-cyan-400">
                Créer le briefing
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {items === null ? (
        <div className="py-16 text-center text-slate-500">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="glass rounded-2xl py-16 text-center">
          <ClipboardList className="mx-auto mb-3 h-10 w-10 text-slate-600" />
          <p className="text-slate-400">Aucun briefing. Assurez-vous d'avoir un avion et une route, puis créez un briefing.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((b) => (
            <div key={b.id} data-testid={`briefing-card-${b.id}`}
              className="glass group cursor-pointer rounded-2xl p-5 transition-all hover:border-cyan-500/50 hover:shadow-[0_0_20px_rgba(0,229,255,0.15)]"
              onClick={() => navigate(`/briefings/${b.id}`)}>
              <div className="flex items-start justify-between">
                <div className="font-display text-base font-semibold text-slate-100">{b.name}</div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`delete-briefing-${b.id}`} onClick={(e) => e.stopPropagation()}
                      className="rounded-lg p-2 text-slate-400 hover:bg-rose-500/10 hover:text-rose-300"><Trash2 className="h-4 w-4" /></button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="border-slate-700 bg-[#0D1527] text-slate-100" onClick={(e) => e.stopPropagation()}>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Supprimer ce briefing ?</AlertDialogTitle>
                      <AlertDialogDescription className="text-slate-400">{b.name} sera supprimé.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="border-slate-700 bg-transparent text-slate-300">Annuler</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(b.id)} className="bg-rose-600 hover:bg-rose-500">Supprimer</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
              <div className="mt-2 flex items-center gap-1.5 font-mono-data text-sm text-cyan-300">
                <span>{b.departure || "?"}</span><ArrowRight className="h-3.5 w-3.5 text-slate-500" /><span>{b.arrival || "?"}</span>
              </div>
              <div className="mt-4 flex items-center gap-3 border-t border-slate-800 pt-3 text-xs text-slate-500">
                <span className="flex items-center gap-1"><RouteIcon className="h-3.5 w-3.5" /> {b.legs?.length || 0} branches</span>
                <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {(b.updated_at || "").slice(0, 10)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {shared.length > 0 && (
        <div className="space-y-4 pt-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-amber-500/80">
            <GraduationCap className="h-3.5 w-3.5" /> Partagés avec moi
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {shared.map((b) => (
              <a key={b.id} href={`/shared/${b.share_token}`} target="_blank" rel="noopener noreferrer"
                data-testid={`shared-card-${b.id}`}
                className="glass group rounded-2xl border-amber-500/20 p-5 transition-all hover:border-amber-500/50 hover:shadow-[0_0_20px_rgba(255,176,0,0.12)]">
                <div className="font-display text-base font-semibold text-slate-100">{b.name}</div>
                <div className="mt-2 flex items-center gap-1.5 font-mono-data text-sm text-cyan-300">
                  <span>{b.departure || "?"}</span><ArrowRight className="h-3.5 w-3.5 text-slate-500" /><span>{b.arrival || "?"}</span>
                </div>
                <div className="mt-4 border-t border-slate-800 pt-3 text-xs text-slate-500">
                  Élève : {b.owner_email}
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
